import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import type { Badge as BadgeType } from '@/types/admin';

const BadgeManagement = () => {
  const [badges, setBadges] = useState<BadgeType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    badge_key: string;
    name: string;
    description: string;
    icon: string;
    requirement: number;
    type: 'reviews' | 'points' | 'tenure' | 'special';
    color: string;
  }>({
    badge_key: '',
    name: '',
    description: '',
    icon: '',
    requirement: 0,
    type: 'reviews',
    color: 'bg-gray-500'
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const { data, error } = await supabase
        .from('badges')
        .select('*')
        .order('type', { ascending: true });

      if (error) throw error;
      setBadges((data || []) as BadgeType[]);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: '載入失敗',
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingId === 'new') {
        const { error } = await supabase
          .from('badges')
          .insert(formData);

        if (error) throw error;
        toast({ title: '新增成功', description: '勳章已新增' });
      } else {
        const { error } = await supabase
          .from('badges')
          .update(formData)
          .eq('id', editingId);

        if (error) throw error;
        toast({ title: '更新成功', description: '勳章已更新' });
      }

      setEditingId(null);
      resetForm();
      fetchBadges();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: '操作失敗',
        description: error.message,
      });
    }
  };

  const handleEdit = (badge: BadgeType) => {
    setEditingId(badge.id);
    setFormData({
      badge_key: badge.badge_key,
      name: badge.name,
      description: badge.description,
      icon: badge.icon,
      requirement: badge.requirement,
      type: badge.type,
      color: badge.color
    });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('確定要刪除此勳章嗎？')) return;

    try {
      const { error } = await supabase
        .from('badges')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: '刪除成功', description: '勳章已刪除' });
      fetchBadges();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: '刪除失敗',
        description: error.message,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      badge_key: '',
      name: '',
      description: '',
      icon: '',
      requirement: 0,
      type: 'reviews',
      color: 'bg-gray-500'
    });
  };

  const getTypeLabel = (type: string) => {
    const labels = {
      reviews: '評論成就',
      points: '積分成就',
      tenure: '時間成就',
      special: '特殊成就'
    };
    return labels[type as keyof typeof labels] || type;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">勳章管理</h1>
        <Button onClick={() => setEditingId('new')}>
          <Plus className="w-4 h-4 mr-2" />
          新增勳章
        </Button>
      </div>

      {editingId && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId === 'new' ? '新增勳章' : '編輯勳章'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">勳章代碼</label>
                <Input
                  value={formData.badge_key}
                  onChange={(e) => setFormData(prev => ({ ...prev, badge_key: e.target.value }))}
                  placeholder="例如：first_review"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">勳章名稱</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">圖示</label>
                <Input
                  value={formData.icon}
                  onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                  placeholder="例如：🏠"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">獲得條件</label>
                <Input
                  type="number"
                  value={formData.requirement}
                  onChange={(e) => setFormData(prev => ({ ...prev, requirement: parseInt(e.target.value) }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">勳章類型</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'reviews' | 'points' | 'tenure' | 'special' }))}
                  className="w-full p-2 border rounded"
                >
                  <option value="reviews">評論成就</option>
                  <option value="points">積分成就</option>
                  <option value="tenure">時間成就</option>
                  <option value="special">特殊成就</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">顏色樣式</label>
                <select
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full p-2 border rounded"
                >
                  <option value="bg-gray-500">灰色</option>
                  <option value="bg-blue-500">藍色</option>
                  <option value="bg-purple-500">紫色</option>
                  <option value="bg-orange-500">橙色</option>
                  <option value="bg-yellow-500">黃色</option>
                  <option value="bg-green-500">綠色</option>
                  <option value="bg-red-500">紅色</option>
                  <option value="bg-indigo-500">靛色</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">勳章描述</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                儲存
              </Button>
              <Button variant="outline" onClick={() => {
                setEditingId(null);
                resetForm();
              }}>
                取消
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {badges.map((badge) => (
          <Card key={badge.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 ${badge.color} rounded-full flex items-center justify-center text-white text-xl`}>
                    {badge.icon}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold">{badge.name}</h3>
                      <Badge variant="outline">{getTypeLabel(badge.type)}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{badge.description}</p>
                    <p className="text-sm font-medium">
                      獲得條件：{badge.requirement} {badge.type === 'reviews' ? '篇評論' : badge.type === 'points' ? '積分' : badge.type === 'tenure' ? '天' : '項目'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(badge)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(badge.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default BadgeManagement;
